import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await register(email, password, name);
        setLoading(false);
        if (res.ok) {
            toast.success("Access granted.");
            navigate("/");
        } else {
            setError(res.error);
        }
    };

    return (
        <div data-testid="register-page" className="mx-auto grid min-h-[calc(100vh-80px)] max-w-6xl grid-cols-1 md:grid-cols-2 gap-16 px-6 py-16 lg:px-10">
            <div className="hidden md:flex flex-col justify-between border border-border p-10 scanlines bg-card">
                <div>
                    <div className="overline">// Onboarding</div>
                    <h1 className="mt-6 font-serif text-6xl leading-[0.95]">
                        Every machine<br />
                        has a{" "}
                        <span className="italic text-phosphor-amber glow-amber">story.</span>
                    </h1>
                    <p className="mt-8 font-sans text-sm text-muted-foreground max-w-sm">
                        Register to browse the archive with a personal account. Admin
                        privileges are granted separately by the curator.
                    </p>
                </div>
                <div className="font-mono text-xs text-muted-foreground">
                    &gt; Session encrypted end-to-end.
                </div>
            </div>

            <div className="flex flex-col justify-center">
                <div className="overline">// Sign up</div>
                <h2 className="mt-4 font-serif text-5xl">Join the archive</h2>

                <form onSubmit={submit} className="mt-10 space-y-6" data-testid="register-form">
                    <label className="block">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Name</div>
                        <input
                            data-testid="register-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber"
                            placeholder="Ada Lovelace"
                        />
                    </label>
                    <label className="block">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Email</div>
                        <input
                            data-testid="register-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber"
                            placeholder="you@archive.io"
                        />
                    </label>
                    <label className="block">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Password</div>
                        <input
                            data-testid="register-password"
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber"
                            placeholder="min. 6 characters"
                        />
                    </label>

                    {error && (
                        <div data-testid="register-error" className="border border-destructive/60 bg-destructive/10 px-4 py-3 font-mono text-xs text-destructive">
                            &gt; {error}
                        </div>
                    )}

                    <button
                        data-testid="register-submit"
                        type="submit"
                        disabled={loading}
                        className="w-full border border-phosphor-amber bg-phosphor-amber px-6 py-3 font-mono text-xs uppercase tracking-[0.3em] text-background hover:bg-transparent hover:text-phosphor-amber transition-colors disabled:opacity-50"
                    >
                        {loading ? "Creating account…" : "Create account ▸"}
                    </button>
                </form>

                <div className="mt-8 font-mono text-xs text-muted-foreground">
                    Already registered?{" "}
                    <Link to="/login" data-testid="link-to-login" className="text-phosphor-amber hover:underline">
                        Log in
                    </Link>
                </div>
            </div>
        </div>
    );
}
